// Head-to-Head derivation core, shared by the HeadToHead panel and the AI
// briefing payload builder in App.jsx (so the coach can be fed even when the
// panel is closed). Everything here is pure: drivers in, delta rows out.
//
// Per-corner seconds are computed from the lap traces (the metrics API has no
// per-corner timing): each lap's distance→time lookup is interpolated over the
// corner's entry/exit band. Because every session measures lap distance along
// its own GPS line, all laps are first rescaled onto the primary session's lap
// length — without this, mid-lap drift swamps the deltas.

import { buildLookup, normalizeLookup, interpolateRelTime, lapDisplayNumber } from './lapDelta';

export const TB_LAP_INDEX = -1;

export function segTime(lookup, a, b) {
  const ta = interpolateRelTime(lookup, a);
  const tb = interpolateRelTime(lookup, b);
  return ta == null || tb == null ? null : tb - ta;
}

// Best (fastest) selected real lap for one driver; TB is synthetic — excluded.
export function bestSelectedLap(lapTraces, selectedLaps) {
  let best = null;
  for (const idx of selectedLaps || []) {
    if (idx === TB_LAP_INDEX) continue;
    const trace = lapTraces?.[idx];
    if (!trace?.points?.length || trace.lap_time_seconds == null) continue;
    if (!best || trace.lap_time_seconds < best.trace.lap_time_seconds) best = { trace, lapIndex: idx };
  }
  return best;
}

// Metric value for a driver's best lap in a corner (specific lap, agg fallback).
// `def` needs { key, phase? }.
export function metricVal(d, cid, def) {
  const bc = d.metrics?.by_corner?.[cid];
  if (!bc) return null;
  const pl = bc.per_lap?.find(p => p.lap_index === d.best?.lapIndex);
  if (def.phase) return pl?.phase?.[def.key] ?? null;
  return pl?.[def.key] ?? bc.agg?.[def.key]?.mean ?? null;
}

// The best-lap phase dict for a driver in a corner (speed profile, coast, …).
export function phaseFor(d, cid) {
  return d.metrics?.by_corner?.[cid]?.per_lap
    ?.find(p => p.lap_index === d.best?.lapIndex)?.phase ?? null;
}

function apexFor(d, cid, fallback) {
  const pl = d.metrics?.by_corner?.[cid]?.per_lap?.find(p => p.lap_index === d.best?.lapIndex);
  return pl?.apex_dist_m ?? fallback;
}

/**
 * @param primary  { name, accent, session, lapTraces, selectedLaps, metrics,
 *                   metricsLoading, onCalculate }
 * @param compareDrivers the App-level descriptor array (slot, name, accent, …)
 * @param corners  corner definitions (entry_dist_m / exit_dist_m / apex lat-lon)
 * @returns { drivers, ready, ref, others, rows, straightsRow, maxAbs }
 *   rows: [{ corner, e, x, cells: [{ driver, total, entry, exit }] }]
 *   (e/x = the corner's distance band in the primary session's coordinates)
 */
export function computeHeadToHeadData({ primary, compareDrivers = [], corners = [] }) {
  const drivers = [];
  if (primary?.session) {
    drivers.push({
      key: 'p', name: primary.name, accent: primary.accent,
      session: primary.session,
      laps: primary.session.laps,
      lapTraces: primary.lapTraces, selectedLaps: primary.selectedLaps,
      metrics: primary.metrics, metricsLoading: primary.metricsLoading,
      onCalculate: primary.onCalculate,
      best: bestSelectedLap(primary.lapTraces, primary.selectedLaps),
    });
  }
  for (const d of compareDrivers) {
    drivers.push({
      key: `c${d.slot}`, name: d.name, accent: d.accent,
      session: d.session,
      laps: d.session?.laps, lapTraces: d.lapTraces, selectedLaps: d.selectedLaps,
      metrics: d.metrics, metricsLoading: d.metricsLoading, onCalculate: d.onCalculate,
      best: bestSelectedLap(d.lapTraces, d.selectedLaps),
    });
  }
  for (const d of drivers) {
    d.lookup = d.best ? normalizeLookup(buildLookup(d.best.trace.points)) : null;
    d.lapNo = d.best ? lapDisplayNumber(d.laps, d.best.lapIndex, d.best.trace.lap_number) : null;
    d.rawLen = d.lookup?.length ? d.lookup[d.lookup.length - 1].distM : null;
  }
  // Rescale every lap onto the first available driver's (normally the primary
  // session's) lap length — corner bands live in its coordinates.
  const canonical = drivers.find(d => d.rawLen);
  for (const d of drivers) {
    d.scale = d.rawLen && canonical ? canonical.rawLen / d.rawLen : 1;
    if (d.lookup && d.scale !== 1) {
      d.lookup = d.lookup.map(p => ({ distM: p.distM * d.scale, relTime: p.relTime }));
    }
  }

  const ready = drivers.filter(d => d.best && d.lookup?.length > 1);
  const ref = ready.length
    ? ready.reduce((b, d) => d.best.trace.lap_time_seconds < b.best.trace.lap_time_seconds ? d : b)
    : null;
  const others = ready.filter(d => d !== ref);
  if (!ref || !others.length) {
    return { drivers, ready, ref, others, rows: [], straightsRow: null, straightRows: [], maxAbs: 0 };
  }

  // Distance band per corner: apex position ± the stored spans. A corner def's
  // entry_dist_m / exit_dist_m are RELATIVE spans (metres from the apex back to
  // entry / forward to exit — see backend corner_detect.py), NOT absolute lap
  // positions. The apex's absolute position comes from the canonical driver's
  // corner metrics (per-lap apex_dist_m) or, before metrics exist, from
  // projecting the apex GPS point onto the canonical lap. Overlapping
  // neighbours are clamped to their midpoint so no stretch of track is counted
  // in two rows.
  const canonicalDrv = drivers.find(d => d.lookup?.length && d.best);
  const apexDistByGps = (c) => {
    const pts = canonicalDrv?.best.trace.points;
    if (!pts || c.apex_lat == null) return null;
    const cosLat = Math.cos((c.apex_lat * Math.PI) / 180);
    let bestD = null, bestErr = Infinity;
    for (const p of pts) {
      if (p.lat == null || p.dist_m == null) continue;
      const err = (p.lat - c.apex_lat) ** 2 + ((p.lon - c.apex_lon) * cosLat) ** 2;
      if (err < bestErr) { bestErr = err; bestD = p.dist_m; }
    }
    return bestD != null ? bestD * (canonicalDrv.scale ?? 1) : null;
  };
  const apexAbsFor = (c) => {
    const raw = apexFor(canonicalDrv, c.id, null);
    if (raw != null) return raw * (canonicalDrv.scale ?? 1);
    return apexDistByGps(c);
  };
  const bands = corners.map(c => {
    const apex = apexAbsFor(c);
    if (apex == null) return null;
    const eSpan = c.entry_dist_m > 0 ? c.entry_dist_m : 100;
    const xSpan = c.exit_dist_m > 0 ? c.exit_dist_m : 100;
    return { c, apex, e: Math.max(0, apex - eSpan), x: apex + xSpan };
  }).filter(Boolean).sort((a, b) => a.e - b.e);
  for (let i = 0; i + 1 < bands.length; i++) {
    if (bands[i].x > bands[i + 1].e) {
      const mid = (bands[i].x + bands[i + 1].e) / 2;
      bands[i].x = mid;
      bands[i + 1].e = mid;
    }
  }

  const rows = bands.map(({ c, apex, e, x }) => {
    // Entry/exit split at the apex, clamped inside the (possibly trimmed) band.
    const split = Math.min(x, Math.max(e, apex));
    const refSeg = segTime(ref.lookup, e, x);
    const refEntry = segTime(ref.lookup, e, split);
    const cells = others.map(d => {
      const seg = segTime(d.lookup, e, x);
      const total = seg == null || refSeg == null ? null : seg - refSeg;
      const entrySeg = segTime(d.lookup, e, split);
      const entry = entrySeg == null || refEntry == null ? null : entrySeg - refEntry;
      return { driver: d, total, entry, exit: total != null && entry != null ? total - entry : null };
    });
    return { corner: c, e, x, cells };
  });

  // Straight tiles: the gaps between consecutive corner bands, computed with
  // the same segTime math as the corner rows — so corner rows + straight tiles
  // telescope to the full lap gap. The run from the last corner to the line
  // and from the line to the first corner is ONE physical straight (it just
  // crosses S/F), so those two pieces combine into a single tile.
  const lapLen = canonical?.rawLen ?? null;
  const bandLabel = (b) => b.c.label || `T${b.c.id}`;
  const straightRows = [];
  if (bands.length && lapLen) {
    // Per-driver delta summed over a tile's segment(s); null if any segment
    // can't be interpolated for either lap.
    const tileCells = (segs) => others.map(d => {
      let total = 0;
      for (const [a, b] of segs) {
        const seg = segTime(d.lookup, a, b);
        const refSeg = segTime(ref.lookup, a, b);
        if (seg == null || refSeg == null) { total = null; break; }
        total += seg - refSeg;
      }
      return { driver: d, total };
    });
    for (let i = 0; i + 1 < bands.length; i++) {
      const a = bands[i].x, b = bands[i + 1].e;
      if (!(b - a > 0)) continue;
      straightRows.push({
        label: `${bandLabel(bands[i])} → ${bandLabel(bands[i + 1])}`,
        isSF: false, e: a, x: b, len_m: b - a,
        cells: tileCells([[a, b]]),
      });
    }
    const first = bands[0], last = bands[bands.length - 1];
    const sfSegs = [];
    if (lapLen - last.x > 0) sfSegs.push([last.x, lapLen]);
    if (first.e > 0) sfSegs.push([0, first.e]);
    const sfLen = sfSegs.reduce((s, [a, b]) => s + (b - a), 0);
    if (sfLen > 0) {
      // e/x = the longer piece, so the ⌖ zoom shows most of the straight (a
      // wrapping range can't be expressed as one zoom band).
      const zoomSeg = sfSegs.reduce((m, s) => (s[1] - s[0] > m[1] - m[0] ? s : m));
      straightRows.push({
        label: `${bandLabel(last)} → ${bandLabel(first)}`,
        isSF: true, e: zoomSeg[0], x: zoomSeg[1], len_m: sfLen,
        cells: tileCells(sfSegs),
      });
    }
  }

  // Residual bucket so the rows sum to the scoreboard gap.
  const straightsRow = {
    cells: others.map(d => {
      const totalGap = d.best.trace.lap_time_seconds - ref.best.trace.lap_time_seconds;
      const sum = rows.reduce((acc, r) => {
        const cell = r.cells.find(cl => cl.driver === d);
        return cell?.total != null ? acc + cell.total : acc;
      }, 0);
      return { driver: d, total: totalGap - sum };
    }),
  };
  const maxAbs = Math.max(0.001, ...rows.flatMap(r => r.cells.map(cl => Math.abs(cl.total ?? 0))),
                          ...straightsRow.cells.map(cl => Math.abs(cl.total ?? 0)));
  return { drivers, ready, ref, others, rows, straightsRow, straightRows, maxAbs };
}

// Compact JSON for the AI briefing (POST /api/briefing, mode "headtohead").
// Python composes the coaching facts from this; the model only phrases them.
const PAYLOAD_TECH = [
  { key: 'min_speed',            out: 'min_speed_delta_mph' },
  { key: 'brake_app_dist_m',     out: 'brake_point_delta_m' },
  { key: 'exit_speed',           out: 'exit_speed_delta_mph' },
  { key: 'coast_s',              out: 'coast_delta_s',   phase: true },
  { key: 'brake_release_dist_m', out: 'release_delta_m', phase: true },
  { key: 'peak_brake_g',         out: 'peak_brake_delta_g' },
];

// `focusKey` picks which driver the coaching addresses (payload `is_primary` —
// the backend's focus flag): default is the panel's primary session driver,
// but any compared driver works, e.g. coaching a slower guest against the
// logged-in user's reference lap. The backend already handles both directions,
// including focus === reference (coach them on where the others are quicker).
export function buildHeadToHeadPayload({ ready, ref, rows, straightsRow, straightRows = [], focusKey = 'p' }) {
  if (!ref || !rows.length) return null;
  const r3 = v => (v == null ? null : Math.round(v * 1000) / 1000);
  const labelOf = r => r.corner.label || `T${r.corner.id}`;
  const focus = ready.find(d => d.key === focusKey) || ready.find(d => d.key === 'p') || ref;

  const drivers = ready.map(d => ({
    name: d.name,
    is_primary: d === focus,
    lap_time_s: r3(d.best.trace.lap_time_seconds),
    gap_s: r3(d.best.trace.lap_time_seconds - ref.best.trace.lap_time_seconds),
    straights_s: d === ref ? 0 : r3(straightsRow?.cells.find(cl => cl.driver === d)?.total),
    corners: d === ref ? [] : rows.map(r => {
      const cl = r.cells.find(c => c.driver === d);
      return { label: labelOf(r), delta_s: r3(cl?.total), entry_s: r3(cl?.entry), exit_s: r3(cl?.exit) };
    }),
    // Per-straight breakdown so the coach can name WHICH straight (and its
    // length lets Python compute "the longest straight" superlative).
    straights: d === ref ? [] : straightRows.map(t => ({
      label: t.label,
      delta_s: r3(t.cells.find(c => c.driver === d)?.total),
      len_m: Math.round(t.len_m),
    })),
  }));

  // Technique deltas: focus vs reference (empty when the focus IS the ref).
  const tech = focus === ref ? [] : rows.map(r => {
    const o = { label: labelOf(r) };
    for (const t of PAYLOAD_TECH) {
      const pv = metricVal(focus, r.corner.id, t);
      const rv = metricVal(ref, r.corner.id, t);
      o[t.out] = pv == null || rv == null ? null : Math.round((pv - rv) * 100) / 100;
    }
    return o;
  });

  return {
    reference: { name: ref.name, lap_time_s: r3(ref.best.trace.lap_time_seconds) },
    drivers,
    tech,
  };
}
